import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSetupStatus, useUpdateMarket, useReconnectConnector, useJobHistory, useTriggerBackfill } from '../hooks/useSDI'
import { MarketTabsLayout } from '../components/sdi/shared/MarketTabsLayout'
import type { ConnectorStatus, JobRunItem } from '../hooks/sdi.types'
import {
  Plug, CheckCircle2, XCircle, AlertCircle, RefreshCw,
  Trash2, ChevronRight, X, Save, Search, Copy, Play,
  Clock, Stethoscope, Download, Loader2,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const connCfg: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  connected: { label: 'Connecté', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  pending: { label: 'En attente', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: AlertCircle },
  error: { label: 'Erreur', color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle },
  never_synced: { label: 'Non connecté', color: 'text-ink-faint bg-paper border-ink-border', icon: Plug },
}

const jobColors: Record<string, string> = {
  ok: 'text-emerald-600 bg-emerald-50', running: 'text-blue-600 bg-blue-50 animate-pulse',
  error: 'text-red-600 bg-red-50', skipped: 'text-ink-faint bg-paper',
}

// ── Token Countdown ──────────────────────────────────────────

function TokenCountdown({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return null
  const hoursLeft = Math.max(0, (new Date(expiresAt).getTime() - Date.now()) / 3600000)
  if (hoursLeft > 24) return null
  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded', hoursLeft < 6 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>
      <Clock size={10} className="inline mr-0.5" />{Math.round(hoursLeft)}h restantes
    </span>
  )
}

// ── Completeness ─────────────────────────────────────────────

function CompletenessBar({ completeness }: { completeness: { has_keywords: boolean; has_google_ads: boolean; has_keyword_planner: boolean; has_ads_data: boolean; has_insight: boolean; pct: number } }) {
  const steps = [
    { label: 'Mots-clés', done: completeness.has_keywords },
    { label: 'Google Ads', done: completeness.has_google_ads },
    { label: 'Keyword Planner', done: completeness.has_keyword_planner },
    { label: 'Données Ads', done: completeness.has_ads_data },
    { label: 'Insight généré', done: completeness.has_insight },
  ]
  return (
    <div className="bg-white rounded-card border border-ink-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-ink">Progression</h3>
        <span className="text-sm font-bold text-brick">{completeness.pct}%</span>
      </div>
      <div className="w-full h-2 bg-paper-warm rounded-full overflow-hidden mb-4">
        <div className="h-full bg-brick rounded-full transition-all duration-500" style={{ width: `${completeness.pct}%` }} />
      </div>
      <div className="grid grid-cols-5 gap-2">
        {steps.map((s) => (
          <div key={s.label} className="text-center">
            <div className={cn('w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1', s.done ? 'bg-emerald-100' : 'bg-paper-warm')}>
              {s.done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <div className="w-3 h-3 rounded-full bg-ink-border" />}
            </div>
            <p className={cn('text-[10px]', s.done ? 'text-emerald-600 font-medium' : 'text-ink-faint')}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Market Params Form ───────────────────────────────────────

function MarketParamsForm({ market, onSave, isPending }: {
  market: { name: string; country: string; language: string; currency: string; seed_keyword: string | null }
  onSave: (data: Record<string, string>) => void
  isPending: boolean
}) {
  const [form, setForm] = useState({ name: market.name, country: market.country, language: market.language, currency: market.currency, seed_keyword: market.seed_keyword ?? '' })
  const [dirty, setDirty] = useState(false)

  const handleChange = (key: string, value: string) => { setForm((p) => ({ ...p, [key]: value })); setDirty(true) }

  return (
    <div className="bg-white rounded-card border border-ink-border p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink">Paramètres du marché</h3>
        {dirty && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Non sauvegardé</span>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[
          { key: 'name', label: 'Nom', span: true },
          { key: 'country', label: 'Pays (ISO 2)', max: 2 },
          { key: 'language', label: 'Langue', max: 5 },
          { key: 'currency', label: 'Devise', max: 3 },
          { key: 'seed_keyword', label: 'Mot-clé graine', span: true },
        ].map((f) => (
          <div key={f.key} className={f.span ? 'col-span-2' : ''}>
            <label className="block text-xs font-medium text-ink-muted mb-1">{f.label}</label>
            <input type="text" value={(form as Record<string, string>)[f.key]} maxLength={f.max}
              onChange={(e) => handleChange(f.key, e.target.value)}
              className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
          </div>
        ))}
      </div>
      <button onClick={() => { onSave(form); setDirty(false) }} disabled={!dirty || isPending}
        className="mt-4 flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
        {isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Sauvegarder
      </button>
    </div>
  )
}

// ── Diagnostics Panel ────────────────────────────────────────

function DiagnosticsPanel({ connectors, completeness }: {
  connectors: ConnectorStatus[]
  completeness: { has_keywords: boolean; has_ads_data: boolean; has_insight: boolean }
}) {
  const [running, setRunning] = useState(false)
  const [results, setResults] = useState<Array<{ label: string; ok: boolean; message: string }>>([])

  const runDiagnostics = () => {
    setRunning(true); setResults([])
    const checks = [
      { label: 'Connecteur santé', ok: connectors.some((c) => c.status === 'connected'), message: connectors.some((c) => c.status === 'connected') ? 'Au moins un connecteur actif' : 'Aucun connecteur — connectez Google Ads' },
      { label: 'Fraîcheur données', ok: connectors.some((c) => c.last_sync_at && (Date.now() - new Date(c.last_sync_at).getTime()) < 14 * 86400000), message: 'Dernière sync < 14 jours' },
      { label: 'Qualité cluster', ok: completeness.has_keywords, message: completeness.has_keywords ? 'Mots-clés présents' : 'Aucun mot-clé — ajoutez des keywords' },
      { label: 'Insight disponible', ok: completeness.has_insight, message: completeness.has_insight ? 'Insight à jour' : 'Pas d\'insight — lancez une sync' },
    ]
    checks.forEach((check, i) => {
      setTimeout(() => {
        setResults((prev) => [...prev, check])
        if (i === checks.length - 1) setRunning(false)
      }, (i + 1) * 500)
    })
  }

  const passed = results.filter((r) => r.ok).length

  return (
    <div className="bg-white rounded-card border border-ink-border p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink">Diagnostics</h3>
        <button onClick={runDiagnostics} disabled={running}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brick bg-gold-light hover:bg-gold-light rounded-lg disabled:opacity-50">
          {running ? <Loader2 size={14} className="animate-spin" /> : <Stethoscope size={14} />}
          {running ? 'En cours…' : 'Lancer les diagnostics'}
        </button>
      </div>
      {results.length > 0 && (
        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              {r.ok ? <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={16} className="text-red-500 flex-shrink-0" />}
              <div className="flex-1">
                <p className="text-xs font-medium text-ink">{r.label}</p>
                <p className="text-[10px] text-ink-faint">{r.message}</p>
              </div>
            </div>
          ))}
          {!running && results.length === 4 && (
            <div className={cn('mt-3 px-3 py-2 rounded-lg text-xs font-medium',
              passed === 4 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')}>
              {passed}/4 checks passés {passed < 4 && '— actions recommandées ci-dessus'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Logs Drawer ──────────────────────────────────────────────

function LogsDrawer({ jobName, marketId, onClose }: { jobName: string; marketId: string; onClose: () => void }) {
  const { data: logs } = useJobHistory(marketId, { job_name: jobName, limit: 20 })
  const [search, setSearch] = useState('')
  const filtered = ((logs ?? []) as JobRunItem[]).filter((l) => !search || l.job_name.includes(search) || (l.error_message ?? '').includes(search))

  return (
    <div className="fixed inset-y-0 right-0 w-[420px] bg-white border-l border-ink-border shadow-elevated z-40 animate-slide-in-right">
      <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border">
        <h3 className="text-sm font-semibold text-ink">Logs — {jobName}</h3>
        <div className="flex items-center gap-2">
          <button className="p-1.5 rounded-lg hover:bg-paper-warm text-ink-faint" title="Exporter"><Download size={14} /></button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
        </div>
      </div>
      <div className="px-5 py-3 border-b border-ink-border">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher dans les logs…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
      </div>
      <div className="overflow-y-auto h-[calc(100%-120px)]">
        {filtered.length === 0 ? (
          <p className="p-5 text-sm text-ink-faint">Aucun log</p>
        ) : filtered.map((log) => (
          <div key={log.id} className={cn('px-5 py-3 border-b border-ink-border/30',
            log.status === 'error' && 'bg-red-50/30', log.status === 'ok' && 'bg-emerald-50/20')}>
            <div className="flex items-center justify-between mb-1">
              <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium', jobColors[log.status])}>{log.status}</span>
              <span className="text-[10px] text-ink-faint">{new Date(log.started_at).toLocaleString('fr-FR')}</span>
            </div>
            <p className="text-xs text-ink">{log.rows_processed} ligne(s){log.ended_at && ` · ${Math.round((new Date(log.ended_at).getTime() - new Date(log.started_at).getTime()) / 1000)}s`}</p>
            {log.error_message && <p className="text-xs text-red-600 mt-1 font-mono bg-red-50 rounded px-2 py-1">{log.error_message}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Deactivate Modal ─────────────────────────────────────────

function DeactivateModal({ onConfirm, onClose }: { onConfirm: () => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-sm p-6 animate-slide-up">
        <h3 className="text-lg font-semibold text-ink mb-2">Archiver ce marché ?</h3>
        <p className="text-sm text-ink-muted mb-6">Les données seront conservées mais les syncs automatiques seront désactivées.</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">Archiver</button>
        </div>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────

export function SetupPage() {
  const { id: marketId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: setup, isLoading } = useSetupStatus(marketId!)
  const updateMarket = useUpdateMarket()
  const reconnect = useReconnectConnector()
  const backfill = useTriggerBackfill()
  const [logsJob, setLogsJob] = useState<string | null>(null)
  const [showDeactivate, setShowDeactivate] = useState(false)

  if (isLoading || !setup) {
    return (
      <MarketTabsLayout>
        <div className="space-y-4 animate-pulse">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 bg-paper-warm rounded-card" />)}</div>
      </MarketTabsLayout>
    )
  }

  const handleSave = (data: Record<string, string>) => updateMarket.mutate({ id: marketId!, ...data })
  const handleDeactivate = () => { updateMarket.mutate({ id: marketId!, status: 'archived' }); setShowDeactivate(false); navigate('/sdi/markets') }
  const handleCopyDiag = () => navigator.clipboard.writeText(`${window.location.origin}/sdi/markets/${marketId}/setup`)

  const connectors = setup.connectors as ConnectorStatus[]
  const jobs = setup.recent_jobs as JobRunItem[]

  return (
    <MarketTabsLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-ink">Configuration & Diagnostic</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopyDiag} className="p-2 rounded-lg text-ink-faint hover:text-brick hover:bg-gold-light" title="Copier le lien diagnostic"><Copy size={16} /></button>
            <button onClick={() => setShowDeactivate(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"><Trash2 size={14} /> Archiver</button>
          </div>
        </div>

        <CompletenessBar completeness={setup.completeness} />
        <MarketParamsForm market={setup.market} onSave={handleSave} isPending={updateMarket.isPending} />
        <DiagnosticsPanel connectors={connectors} completeness={setup.completeness} />

        {/* Connectors Table */}
        <div className="bg-white rounded-card border border-ink-border overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-border"><h3 className="text-sm font-semibold text-ink">Connecteurs</h3></div>
          <div className="divide-y divide-ink-border/30">
            {connectors.map((c) => {
              const cfg = connCfg[c.status] ?? connCfg.never_synced
              return (
                <div key={c.source} className="flex items-center gap-4 px-5 py-4 hover:bg-paper">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center border', cfg.color)}>
                    <cfg.icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink">{c.source.replace(/_/g, ' ')}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium border', cfg.color)}>{cfg.label}</span>
                      <TokenCountdown expiresAt={c.token_expires_at} />
                      {c.last_sync_at && <span className="text-[10px] text-ink-faint">Sync: {new Date(c.last_sync_at).toLocaleDateString('fr-FR')}</span>}
                    </div>
                    {c.last_error && <p className="text-xs text-red-500 mt-1 truncate">{c.last_error}</p>}
                  </div>
                  <button onClick={() => reconnect.mutate({ marketId: marketId!, source: c.source })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brick bg-gold-light hover:bg-gold-light rounded-lg">
                    <RefreshCw size={12} className={reconnect.isPending ? 'animate-spin' : ''} />
                    {c.status === 'never_synced' ? 'Connecter' : 'Reconnecter'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Jobs History */}
        <div className="bg-white rounded-card border border-ink-border overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">Historique des jobs</h3>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-ink-border bg-paper">
              <th className="text-left px-4 py-2.5 font-medium text-ink-muted">Job</th>
              <th className="text-left px-4 py-2.5 font-medium text-ink-muted">Statut</th>
              <th className="text-left px-4 py-2.5 font-medium text-ink-muted">Démarré</th>
              <th className="text-right px-4 py-2.5 font-medium text-ink-muted">Lignes</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody className="divide-y divide-ink-border/30">
              {jobs.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-faint">Aucun job exécuté</td></tr>
              ) : jobs.map((j) => (
                <tr key={j.id} className="hover:bg-paper">
                  <td className="px-4 py-3 font-medium text-ink font-mono text-xs">{j.job_name}</td>
                  <td className="px-4 py-3"><span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium', jobColors[j.status])}>{j.status}</span></td>
                  <td className="px-4 py-3 text-xs text-ink-muted">{new Date(j.started_at).toLocaleString('fr-FR')}</td>
                  <td className="px-4 py-3 text-right text-xs text-ink">{j.rows_processed}</td>
                  <td className="px-4 py-3"><button onClick={() => setLogsJob(j.job_name)} className="text-xs text-brick hover:text-brick-dark flex items-center gap-0.5">Logs <ChevronRight size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-card border border-red-200 p-5">
          <h3 className="text-sm font-semibold text-red-700 mb-3">Zone danger</h3>
          <div className="flex items-center gap-3">
            <button onClick={() => backfill.mutate({ marketId: marketId! })}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink border border-ink-border hover:bg-paper rounded-lg">
              {backfill.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Lancer backfill 24 mois
            </button>
            <button onClick={() => setShowDeactivate(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg">
              <Trash2 size={14} /> Archiver le marché
            </button>
          </div>
        </div>

        {logsJob && <LogsDrawer jobName={logsJob} marketId={marketId!} onClose={() => setLogsJob(null)} />}
        {showDeactivate && <DeactivateModal onConfirm={handleDeactivate} onClose={() => setShowDeactivate(false)} />}
      </div>
    </MarketTabsLayout>
  )
}
